import { useCallback, useEffect, useState } from "react";
import { api, type Screen, type User } from "./api";
import { Credit } from "./Credit";
import { Icon, type IconName } from "./icons";
import { initials } from "./format";
import { Logo } from "./Logo";
import { AccountPage } from "./pages/Account";
import { AuthorizationsPage } from "./pages/Authorizations";
import { HomePage } from "./pages/Home";
import { LoginPage } from "./pages/Login";
import { MediaPage } from "./pages/MediaLibrary";
import { PlaylistsPage } from "./pages/Playlists";
import { ScreensPage } from "./pages/Screens";
import { SupportPage } from "./pages/Support";

type Status = {
  needsSetup: boolean;
  authenticated: boolean;
  storageConfigured: boolean;
};

type Section = "accueil" | "ecrans" | "medias" | "playlists" | "autorisations" | "support" | "compte";

const PRIMARY: { href: string; id: Section; label: string; short: string; icon: IconName }[] = [
  { href: "/", id: "accueil", label: "Accueil", short: "Accueil", icon: "home" },
  { href: "/ecrans", id: "ecrans", label: "Écrans", short: "Écrans", icon: "screen" },
  { href: "/medias", id: "medias", label: "Médias", short: "Médias", icon: "media" },
  { href: "/playlists", id: "playlists", label: "Playlists", short: "Listes", icon: "list" },
];

const SECONDARY: { href: string; id: Section; label: string; icon: IconName }[] = [
  { href: "/autorisations", id: "autorisations", label: "Autorisations", icon: "shield" },
  { href: "/compte", id: "compte", label: "Compte", icon: "user" },
  { href: "/support", id: "support", label: "Support", icon: "life" },
];

const TITLES: Record<Section, string> = {
  accueil: "Accueil",
  ecrans: "Écrans",
  medias: "Médias",
  playlists: "Playlists",
  autorisations: "Autorisations",
  support: "Support",
  compte: "Compte",
};

export function App() {
  const { path, navigate } = useRoute();
  const [status, setStatus] = useState<Status | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [error, setError] = useState("");
  const [pendingDevices, setPendingDevices] = useState(0);
  const [droppedScreens, setDroppedScreens] = useState(0);
  const [more, setMore] = useState(false);

  const refreshSession = useCallback(async () => {
    const next = await api<Status>("/api/auth/status");
    setStatus(next);
    if (!next.authenticated) {
      setUser(null);
      return;
    }
    const me = await api<{ user: User }>("/api/auth/me");
    setUser(me.user);
  }, []);

  useEffect(() => {
    refreshSession().catch((reason: unknown) => {
      setError(reason instanceof Error ? reason.message : "Connexion au serveur impossible");
    });
  }, [refreshSession]);

  useEffect(() => {
    if (!user) return;
    if (path === "/login") navigate("/", true);
  }, [user, path, navigate]);

  useEffect(() => {
    if (!user) return;
    let stop = false;
    async function tick() {
      try {
        const data = await api<{ screens: Screen[] }>("/api/screens");
        if (stop) return;
        setPendingDevices(data.screens.reduce((count, screen) => count + screen.devices.filter((device) => device.status === "pending").length, 0));
        setDroppedScreens(data.screens.filter((screen) => screen.dropped).length);
      } catch {
        if (!stop) {
          setPendingDevices(0);
          setDroppedScreens(0);
        }
      }
    }
    void tick();
    const timer = window.setInterval(() => void tick(), 5000);
    return () => {
      stop = true;
      window.clearInterval(timer);
    };
  }, [user]);

  const section = sectionFrom(path);

  useEffect(() => {
    document.title = user
      ? `${TITLES[section]} — Anytime Fitness Trois-Rivières`
      : "Connexion — Anytime Fitness Trois-Rivières";
  }, [section, user]);

  useEffect(() => {
    setMore(false);
  }, [path]);

  useEffect(() => {
    if (!more) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMore(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [more]);

  async function logout() {
    await api("/api/auth/logout", { method: "POST" });
    setUser(null);
    setStatus((current) => (current ? { ...current, authenticated: false } : current));
    navigate("/login");
  }

  if (error && !status) {
    return (
      <main className="gate">
        <div className="gate-card">
          <Logo />
          <p className="banner error">{error}</p>
          <button type="button" className="primary" onClick={() => window.location.reload()}>Réessayer</button>
          <Credit />
        </div>
      </main>
    );
  }
  if (!status) {
    return (
      <main className="gate">
        <div className="gate-card">
          <Logo />
          <p>Chargement…</p>
          <Credit />
        </div>
      </main>
    );
  }
  if (!user) {
    return (
      <LoginPage
        needsSetup={status.needsSetup}
        onSuccess={(next) => {
          setUser(next);
          setStatus({ ...status, needsSetup: false, authenticated: true });
          navigate("/");
        }}
      />
    );
  }

  const moreActive = section === "autorisations" || section === "support" || section === "compte";

  return (
    <div className="shell">
      <aside className="sidebar">
        <a className="brand" href="/" onClick={(event) => { event.preventDefault(); navigate("/"); }}>
          <Logo />
        </a>
        <p className="sidebar-place">Trois-Rivières</p>
        <nav className="side-nav" aria-label="Sections">
          <p className="nav-label">Club</p>
          {PRIMARY.map((item) => (
            <a
              key={item.href}
              href={item.href}
              className={section === item.id ? "active" : ""}
              aria-current={section === item.id ? "page" : undefined}
              onClick={(event) => { event.preventDefault(); navigate(item.href); }}
            >
              <Icon name={item.icon} />
              <span>{item.label}</span>
              {item.id === "ecrans" && droppedScreens > 0 ? <span className="nav-count alert">{droppedScreens}</span> : null}
            </a>
          ))}
          <p className="nav-label">Réglages</p>
          {SECONDARY.map((item) => (
            <a
              key={item.href}
              href={item.href}
              className={section === item.id ? "active" : ""}
              aria-current={section === item.id ? "page" : undefined}
              onClick={(event) => { event.preventDefault(); navigate(item.href); }}
            >
              <Icon name={item.icon} />
              <span>{item.label}</span>
              {item.id === "autorisations" && pendingDevices > 0 ? <span className="nav-count">{pendingDevices}</span> : null}
            </a>
          ))}
        </nav>
        <div className="sidebar-user">
          <a className="avatar" href="/compte" onClick={(event) => { event.preventDefault(); navigate("/compte"); }} aria-label="Ouvrir le compte">
            {initials(user.name)}
          </a>
          <div className="sidebar-id">
            <strong>{user.name}</strong>
            <span>{user.email}</span>
          </div>
          <button type="button" className="icon-button" aria-label="Se déconnecter" onClick={() => void logout()}>
            <Icon name="logout" />
          </button>
        </div>
      </aside>
      <div className="workspace">
        <header className="mobile-top">
          <a className="brand" href="/" onClick={(event) => { event.preventDefault(); navigate("/"); }}>
            <Logo />
          </a>
          <a className="avatar" href="/compte" onClick={(event) => { event.preventDefault(); navigate("/compte"); }} aria-label={`Compte de ${user.name}`}>
            {initials(user.name)}
          </a>
        </header>
        <main className="content">
          {section === "accueil" ? <HomePage navigate={navigate} /> : null}
          {section === "ecrans" ? <ScreensPage path={path} navigate={navigate} /> : null}
          {section === "medias" ? <MediaPage /> : null}
          {section === "playlists" ? <PlaylistsPage path={path} navigate={navigate} /> : null}
          {section === "autorisations" ? <AuthorizationsPage /> : null}
          {section === "support" ? <SupportPage /> : null}
          {section === "compte" ? <AccountPage user={user} onLogout={logout} /> : null}
        </main>
        <footer className="site-credit">
          <Credit />
        </footer>
        <nav className="tabbar" aria-label="Sections">
          {PRIMARY.map((item) => (
            <a
              key={item.href}
              href={item.href}
              className={section === item.id ? "tab-link active" : "tab-link"}
              aria-current={section === item.id ? "page" : undefined}
              onClick={(event) => { event.preventDefault(); navigate(item.href); }}
            >
              <span className="tab-icon">
                <Icon name={item.icon} />
                {item.id === "ecrans" && droppedScreens > 0 ? <span className="nav-count alert">{droppedScreens}</span> : null}
              </span>
              {item.short}
            </a>
          ))}
          <button
            type="button"
            className={moreActive ? "tab-link active" : "tab-link"}
            aria-expanded={more}
            aria-haspopup="dialog"
            onClick={() => setMore((open) => !open)}
          >
            <span className="tab-icon">
              <Icon name="more" />
              {pendingDevices > 0 ? <span className="nav-count">{pendingDevices}</span> : null}
            </span>
            Plus
          </button>
        </nav>
        {more ? (
          <div className="sheet-backdrop" onClick={() => setMore(false)}>
            <div className="sheet" role="dialog" aria-modal="true" aria-label="Autres sections" onClick={(event) => event.stopPropagation()}>
              <div className="sheet-handle" />
              <p className="sheet-user">{user.name}</p>
              {SECONDARY.map((item) => (
                <a
                  key={item.href}
                  href={item.href}
                  className={section === item.id ? "sheet-link active" : "sheet-link"}
                  onClick={(event) => { event.preventDefault(); navigate(item.href); }}
                >
                  <Icon name={item.icon} />
                  <span>{item.label}</span>
                  {item.id === "autorisations" && pendingDevices > 0 ? <span className="nav-count">{pendingDevices}</span> : null}
                </a>
              ))}
              <button type="button" className="sheet-link danger-link" onClick={() => void logout()}>
                <Icon name="logout" />
                <span>Se déconnecter</span>
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function sectionFrom(path: string): Section {
  if (path.startsWith("/medias")) return "medias";
  if (path.startsWith("/playlists")) return "playlists";
  if (path.startsWith("/autorisations")) return "autorisations";
  if (path.startsWith("/support")) return "support";
  if (path.startsWith("/compte")) return "compte";
  if (path.startsWith("/ecrans")) return "ecrans";
  return "accueil";
}

function useRoute() {
  const [path, setPath] = useState(() => window.location.pathname);
  useEffect(() => {
    const onPop = () => setPath(window.location.pathname);
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  const navigate = useCallback((to: string, replace = false) => {
    const next = new URL(to, window.location.origin);
    const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    const target = `${next.pathname}${next.search}${next.hash}`;
    if (replace) window.history.replaceState({}, "", target);
    else if (target !== current) window.history.pushState({}, "", target);
    setPath(next.pathname);
  }, []);
  return { path, navigate };
}
